import { createContext, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Auth0Provider, useAuth0, type AppState } from '@auth0/auth0-react';
import {
  classifyAuthError,
  clearRecoveryAttempt,
  markRecoveryAttempt,
  recoveryAlreadyAttempted,
} from './authError';
import { devEmail, getAuth0Config, isAuth0Configured, isDevMode } from './config';
import { clearTokenGetter, setAuthErrorHandler, setTokenGetter } from './tokenStore';
import { endSession } from '../api/sessions';

/** Upper bound on the cookie-revocation call during logout, so a hung request can't block sign-out. */
const SESSION_REVOKE_TIMEOUT_MS = 3_000;

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  userEmail: string | null;
  /** Set when auth failed for a reason other than "not signed in" — the app renders an error view. */
  authError: string | null;
  login: () => void;
  logout: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

/** Dev-mode auth: skip Auth0, inject email via X-Dev-User. */
function DevAuthProvider({ children }: { children: ReactNode }) {
  const email = devEmail();
  const getAccessToken = useCallback(async () => 'dev-token', []);

  useEffect(() => {
    setTokenGetter(getAccessToken);
    return () => clearTokenGetter();
  }, [getAccessToken]);

  const value: AuthContextValue = {
    isAuthenticated: Boolean(email),
    isLoading: false,
    userEmail: email || null,
    authError: null,
    login: () => {},
    logout: () => {},
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Real Auth0 path. Token is whatever `getAccessTokenSilently()` returns; the server validates it. */
function Auth0Content({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  // The server validates the access token (audience-scoped to the bevi-ailab API) and reads the email from the
  // namespaced custom claim an Auth0 Action injects. We send that access token as the bearer.
  const getAccessToken = useCallback(async () => {
    try {
      return (await getAccessTokenSilently()) ?? null;
    } catch (err) {
      console.error('[Auth] failed to get access token', err);
      return null;
    }
  }, [getAccessTokenSilently]);

  const handleAuthError = useCallback(() => {
    console.warn('[Auth] 401 received — clearing local state and redirecting to login');
    clearTokenGetter();
    loginWithRedirect({ authorizationParams: { prompt: 'login' } });
  }, [loginWithRedirect]);

  useEffect(() => {
    if (isAuthenticated) {
      setTokenGetter(getAccessToken);
      setAuthErrorHandler(handleAuthError);
    } else {
      clearTokenGetter();
    }
    return () => {
      if (isAuthenticated) clearTokenGetter();
    };
  }, [isAuthenticated, getAccessToken, handleAuthError]);

  const login = useCallback(
    () => loginWithRedirect({ authorizationParams: { prompt: 'login' } }),
    [loginWithRedirect],
  );

  const logout = useCallback(() => {
    // Revoke the static-page session cookie. The request authenticates with the cookie itself, so it works
    // even after the bearer has expired. Best-effort and time-boxed: it never blocks sign-out, and no 401
    // from anything still in flight may trigger the login redirect mid-logout.
    setAuthErrorHandler(() => {});
    endSession(AbortSignal.timeout(SESSION_REVOKE_TIMEOUT_MS))
      .catch((err: unknown) => console.warn('[Auth] could not revoke the session cookie', err))
      .finally(() => {
        clearTokenGetter();
        clearRecoveryAttempt();
        auth0Logout({ logoutParams: { returnTo: window.location.origin } });
      });
  }, [auth0Logout]);

  // Once Auth0 reports authenticated, fetch a token so we know the API call will succeed before we
  // call the session usable. `isAuthenticated` alone is not enough: it is derived from the user object
  // cached in localStorage, and `checkSession()` swallows renewal failures — so a dead refresh token
  // still yields `isAuthenticated: true` while every token call throws. This status must therefore
  // have a terminal failure state; a pending-forever latch renders a permanently blank page.
  const [status, setStatus] = useState<'pending' | 'ready' | 'failed'>('pending');
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getAccessTokenSilently()
      .then(() => {
        if (cancelled) return;
        clearRecoveryAttempt();
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const failure = classifyAuthError(err);
        console.error(`[Auth] could not obtain an access token (${failure.kind})`, err);
        // Dead cached session: drop it locally so `isAuthenticated` flips to false and the app falls
        // through to the login redirect. Guarded to one attempt per browser session so a grant that
        // keeps failing can't become a redirect loop.
        //
        // `openUrl: false` is load-bearing, not just "skip the round-trip": auth0-react only dispatches
        // its LOGOUT action (the one that clears `isAuthenticated`) when the caller passes `openUrl`.
        // Drop it and the local state never flips — straight back to the blank page this fixes.
        if (failure.kind === 'stale-session' && !recoveryAlreadyAttempted()) {
          markRecoveryAttempt();
          auth0Logout({ openUrl: false }).catch((logoutErr: unknown) => {
            console.error('[Auth] failed to clear the dead session', logoutErr);
          });
          return;
        }
        setAuthError(
          failure.kind === 'stale-session'
            ? `Auth0 would not renew your session (${failure.code}), and signing in again did not fix it.`
            : `Could not reach Auth0: ${failure.message}`,
        );
        setStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getAccessTokenSilently, auth0Logout]);

  const value: AuthContextValue = {
    isAuthenticated: isAuthenticated && status === 'ready',
    isLoading: isLoading || (isAuthenticated && status === 'pending'),
    userEmail: user?.email ?? null,
    // Surface Auth0's own init/callback errors too, otherwise a failed callback bounces back to login forever.
    authError: authError ?? (error && !isAuthenticated ? `Sign-in failed: ${error.message}` : null),
    login,
    logout,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function AuthNotConfiguredView() {
  return (
    <div className="not-configured">
      <h1 style={{ marginTop: 0 }}>bevi-ailab: auth not configured</h1>
      <p>This web build has no Auth0 credentials baked in and dev-mode is off.</p>
      <p>Pick one:</p>
      <ul>
        <li>
          <strong>Dev mode</strong> (local only): rebuild with
          <code> VITE_AUTH_DEV_MODE=true VITE_AUTH_DEV_EMAIL=you@bevi.co </code>
          and make sure <code>you@bevi.co</code> is in <code>data/users.txt</code>.
        </li>
        <li>
          <strong>Production</strong>: build with <code>VITE_AUTH0_DOMAIN</code>,
          <code> VITE_AUTH0_CLIENT_ID</code>, <code>VITE_AUTH0_AUDIENCE</code> set.
        </li>
      </ul>
    </div>
  );
}

/** Strip the `?code=…&state=…` Auth0 dropped on us after callback. */
function handleRedirectCallback(appState?: AppState) {
  const targetUrl = appState?.returnTo || window.location.origin;
  window.history.replaceState({}, document.title, targetUrl);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isDevMode()) return <DevAuthProvider>{children}</DevAuthProvider>;
  const config = isAuth0Configured() ? getAuth0Config() : null;
  if (!config) return <AuthNotConfiguredView />;
  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      authorizationParams={{
        redirect_uri: config.redirectUri,
        audience: config.audience,
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={handleRedirectCallback}
    >
      <Auth0Content>{children}</Auth0Content>
    </Auth0Provider>
  );
}
